import {
  Box,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell as TableCellBase,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, memo, ReactNode } from "react";
import { FeedbackType } from "types/feedback";
import dayjs from "dayjs";
import { ErrorOutline } from "@mui/icons-material";
import { ROLES } from "types/user";
import { CoachPetAvatar } from "components/VetStore/CoachPetAvatar";
import { DomainColorSquares } from "components/Requirements";

const TableCell = styled(TableCellBase)`
  border: none;
  padding-bottom: 4px;
  padding-top: 4px;
  &:first-of-type {
    font-weight: 600;
  }
`;

interface FeedbackHeaderItemProps {
  label: string;
  value: string | null;
  isStudent?: boolean;
  adornment?: ReactNode;
}

const formatFeedbackDate = (value?: string | null) =>
  value ? dayjs(value).format("LLL") : null;

const FeedbackHeaderItem: FC<FeedbackHeaderItemProps> = ({
  label,
  value,
  isStudent,
  adornment,
}) => (
  <TableRow>
    <TableCell>{label}</TableCell>
    <TableCell>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {adornment}
        <Typography
          variant="body1"
          color={!value && isStudent ? "textDisabled" : "inherit"}
        >
          {!value && isStudent && <ErrorOutline sx={{ fontSize: 14 }} />}{" "}
          {!!value ? value : isStudent ? "Missing" : "N/A"}
        </Typography>
      </Stack>
    </TableCell>
  </TableRow>
);

type FeedbackHeaderProps = {
  feedback: FeedbackType;
  role?: string;
};

export const FeedbackHeaderComponent: FC<FeedbackHeaderProps> = ({
  feedback,
  role = ROLES.STUDENT,
}) => {
  const isStudent = role === ROLES.STUDENT;
  return (
    <Box>
      <Table>
        <TableBody>
          {feedback.student && (
            <FeedbackHeaderItem
              label="Learner"
              value={feedback.student.name}
              adornment={
                <CoachPetAvatar
                  pictureUrl={feedback.student.picture_url}
                  name={feedback.student.name}
                  equippedStoreItems={feedback.student.equipped_store_items}
                  avatarSize={36}
                />
              }
            />
          )}
          {feedback.coach && (
            <FeedbackHeaderItem
              label="Coach"
              value={feedback.coach.name}
              adornment={
                <CoachPetAvatar
                  pictureUrl={feedback.coach.picture_url}
                  name={feedback.coach.name}
                  equippedStoreItems={feedback.coach.equipped_store_items}
                  avatarSize={36}
                />
              }
            />
          )}

          {feedback.animal_name && (
            <FeedbackHeaderItem
              label="Animal Name"
              value={feedback.animal_name}
            />
          )}

          {feedback.mrn && (
            <FeedbackHeaderItem
              label="Additional information"
              value={feedback.mrn}
            />
          )}

          <FeedbackHeaderItem
            label="Observation Date"
            value={formatFeedbackDate(feedback.observation_date)}
          />
          <FeedbackHeaderItem
            label="Creation Date"
            value={formatFeedbackDate(feedback.created_at)}
          />
          <FeedbackHeaderItem
            label="Site"
            value={feedback.site?.name ?? null}
            isStudent={isStudent}
          />
          <FeedbackHeaderItem
            label="Course or Service"
            value={feedback.course ?? null}
            isStudent={isStudent}
          />

          <FeedbackHeaderItem
            label="Species"
            value={feedback.species?.name ?? null}
            isStudent={isStudent}
          />
          <FeedbackHeaderItem
            label="Sex"
            value={feedback.animal_sex ?? null}
            isStudent={isStudent}
          />
          {feedback.feedback_skills && (
            <TableRow>
              <TableCell>Observed Skills</TableCell>
              <TableCell>
                <Stack spacing={0.5} alignItems="flex-start">
                  <Typography variant="body1">
                    {feedback.feedback_skills[0]?.requirement?.name ||
                      (isStudent ? "Missing" : "N/A")}
                  </Typography>
                  <DomainColorSquares
                    requirement={feedback.feedback_skills[0]?.requirement}
                  />
                </Stack>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export const FeedbackHeader = memo(FeedbackHeaderComponent);
